import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutGrid,
  FolderKanban,
  DollarSign,
  ShoppingCart,
  MapPin,
  Package,
  Shield,
  Users,
  ExternalLink,
  Code,
  Cpu,
  FileStack,
  CalendarCheck,
  Wrench,
  CheckCircle2,
  CalendarRange,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';

const SKILL_GRID_NAV_KEY = 'skill_grid_nav';

/** Systems already supported in our platform (e.g. BCS Carolinas, Vista Analyst, integrations). */
const ALREADY_SUPPORTED_NAMES = new Set([
  'Procore', 'Viewpoint Vista', 'Vista', 'Fulcrum', 'SharePoint', 'ShareDrive',
  'Follow-up CRM', 'Autodesk Build', 'ACC Build', 'Autodesk Construction Build',
  'Autodesk Construction Cloud', 'Fieldwire',
  'Sage Intacct', 'Sage HRMS', 'Coupa', 'RiskCast', 'AIC One Global',
  'Litmos', 'WinEst / DESTINI', 'Nomadis', 'PCDB', 'Egnyte',
  'Touchplan', 'PlanGrid', 'ConstructionOnline',
  'Oracle Primavera P6 EPPM', 'Oracle Primavera Cloud',
  'Bentley SYNCHRO (Schedules API)', 'Microsoft Project / Project Operations',
  'Oracle Aconex',
  'QuickBooks Online', 'Ramp', 'Sage 300 CRE', 'Yardi Voyager',
  'BuildingConnected', 'Felix', 'Prokuria',
  'Google Drive (Shared Drives)',
  'Hilti ON!Track Unite', 'Trackunit',
  'Blackline Safety', 'Intelex', 'Locus Technologies', 'Observia AI',
  'Intenseye', 'SafetyChain', 'SafetyCulture (iAuditor)', 'Emex',
  'HubSpot', 'Salesforce',
  'HCSS (HeavyJob / Stratus)', 'Penta ERP', 'DWG / DXF Parsing',
]);

/**
 * Construction software with open or public API / MCP ??skill grid.
 * Includes: PM, ERP, Procurement, Field, Asset, Safety, Event Management (safety),
 * Documents, Planning & Scheduling, CRM, Custom / in-house.
 */

const CATEGORIES = [
  { id: 'overview', label: 'Overview', icon: LayoutGrid, description: 'All categories' },
  { id: 'pm', label: 'Project Management', icon: FolderKanban, description: 'PM & collaboration' },
  { id: 'planning', label: 'Planning & Scheduling', icon: CalendarRange, description: 'Schedules, P6, Primavera, 4D' },
  { id: 'erp', label: 'ERP & Accounting', icon: DollarSign, description: 'Accounting & job cost' },
  { id: 'procurement', label: 'Procurement', icon: ShoppingCart, description: 'Purchasing & bidding' },
  { id: 'field', label: 'Field Management', icon: MapPin, description: 'Field & plans' },
  { id: 'documents', label: 'Documents', icon: FileStack, description: 'SharePoint, ShareDrive, file storage' },
  { id: 'asset', label: 'Asset Management', icon: Package, description: 'Equipment & tools' },
  { id: 'safety', label: 'Safety & EHS', icon: Shield, description: 'EHS & compliance' },
  { id: 'safetyEvents', label: 'Safety Events', icon: CalendarCheck, description: 'Safety events, incidents, inspections' },
  { id: 'crm', label: 'CRM', icon: Users, description: 'Sales & pipeline' },
  { id: 'training', label: 'Training & LMS', icon: CalendarCheck, description: 'LMS, certifications, compliance training' },
  { id: 'workforce', label: 'Workforce & Housing', icon: Users, description: 'Billeting, housing, timekeeping, HR' },
  { id: 'custom', label: 'Custom / In-House', icon: Wrench, description: 'In-house built & niche software' },
];

const SOFTWARE_BY_CATEGORY = {
  pm: [
    { name: 'Procore', apiType: 'REST', docsUrl: 'https://developers.procore.com/', description: 'Open API, OAuth 2.0; 500+ integrations, ERP connectors.', supported: true },
    { name: 'Autodesk Construction Cloud', apiType: 'REST', docsUrl: 'https://aps.autodesk.com/developer/overview/autodesk-construction-cloud', description: 'APIs for designs, documents, models; Integration Partner Program.', supported: true },
    { name: 'Fieldwire', apiType: 'REST', docsUrl: 'https://developers.fieldwire.com/', description: 'REST API; projects, plans, forms, tasks; US/EU regions.', supported: true },
    { name: 'Touchplan', apiType: 'REST', docsUrl: 'https://touchplan.io/api/', description: 'RESTful API for project data, ERP integration, public docs.', supported: true },
    { name: 'PlanGrid', apiType: 'REST', docsUrl: 'https://developer.plangrid.com/', description: 'API for plans and field data; contact sales for access.', supported: true },
    { name: 'ConstructionOnline', apiType: 'REST', docsUrl: 'https://help.constructiononline.com/developers', description: 'Contacts, projects, estimating, purchasing, invoicing, scheduling.', supported: true },
  ],
  planning: [
    { name: 'Oracle Primavera P6 EPPM', apiType: 'REST', docsUrl: 'https://docs.oracle.com/cd/F37125_01/English/Integration_Documentation/rest_api/D99665.html', description: 'P6 REST API; OAuth; projects, activities, resources; cURL, Java, Node.js.', supported: true },
    { name: 'Oracle Primavera Cloud', apiType: 'MCP + REST', docsUrl: 'https://docs.oracle.com/cd/E80480_01/English/integration/primavera_rest_api/D282648.html', description: 'REST API + MCP; schedule scenarios, leveling, job scheduling; OAuth token.', supported: true },
    { name: 'Bentley SYNCHRO (Schedules API)', apiType: 'REST', docsUrl: 'https://developer.bentley.com/apis/schedules/overview', description: 'iTwin Platform; tasks, resources, 4D data from SYNCHRO Infrastructure Cloud; GET/POST.', supported: true },
    { name: 'Microsoft Project / Project Operations', apiType: 'REST', docsUrl: 'https://docs.microsoft.com/en-us/dynamics365/project-operations/project-management/schedule-api-preview', description: 'Schedule APIs: msdyn_PssCreate/Update/Delete, projects, tasks, dependencies, resources.', supported: true },
    { name: 'Oracle Aconex', apiType: 'REST', docsUrl: 'https://help.aconex.com/apis/api-guide-project/', description: 'Projects API, Documents, Cost; OAuth and Basic Auth; construction collaboration.', supported: true },
    { name: 'Touchplan', apiType: 'REST', docsUrl: 'https://touchplan.io/api/', description: 'RESTful API for schedule and project data; ERP integration; public documentation.', supported: true },
    { name: 'Procore', apiType: 'REST', docsUrl: 'https://developers.procore.com/', description: 'Scheduling integrations with P6, Microsoft Project; schedule data via API.', supported: true },
  ],
  erp: [
    { name: 'QuickBooks Online', apiType: 'MCP + REST', docsUrl: 'https://github.com/intuit/quickbooks-online-mcp-server', description: 'Intuit QBO MCP server; OAuth; accounts, invoices, bills, customers, vendors.', supported: true },
    { name: 'Procore', apiType: 'REST', docsUrl: 'https://developers.procore.com/', description: 'ERP connectors (Vista, Sage, QB, NetSuite, Acumatica, Yardi).', supported: true },
    { name: 'Viewpoint Vista', apiType: 'REST', docsUrl: 'https://developer.trimble.com/docs/viewpoint/introduction', description: 'Trimble REST API; AP/AR, GL, job cost, equipment, payroll, PO.', supported: true },
    { name: 'Ramp', apiType: 'MCP + REST', docsUrl: 'https://docs.ramp.com/developer-api/v1/developer-mcp', description: 'Ramp MCP + Developer API; spend, cards, reimbursements, OAuth.', supported: true },
    { name: 'Sage 300 CRE', apiType: 'REST', docsUrl: 'https://appxchange.trimble.com/connectors/sage-300-cre', description: 'Trimble connectors; AP, GL, job cost, equipment, payroll; Web API docs.', supported: true },
    { name: 'Yardi Voyager', apiType: 'Interfaces', docsUrl: 'https://www.yardi.com/services/interfaces/', description: '300+ interfaces via Standard Interface Partnership Program.', supported: true },
    { name: 'Sage Intacct', apiType: 'MCP + REST', docsUrl: 'https://developer.sage.com/intacct/docs/', description: 'Cloud ERP; REST + XML API + MCP; GL, AP, AR, construction contracts, retainage, change orders.', supported: true },
    { name: 'Penta ERP', apiType: 'REST', docsUrl: 'https://pentaconnectapi.developer.azure-api.net/', description: 'PentaConnect APIs on Azure APIM; jobs, cost codes, forecasts, AP invoices, timecard posting; OAuth 2.0.', supported: true },
  ],
  procurement: [
    { name: 'BuildingConnected', apiType: 'REST', docsUrl: 'https://aps.autodesk.com/buildingconnected-cover-page', description: 'Autodesk; bidding, risk; TradeTapp for subcontractor qualification.', supported: true },
    { name: 'ConstructionOnline', apiType: 'REST', docsUrl: 'https://help.constructiononline.com/developers', description: 'Purchasing, invoicing, change orders via API.', supported: true },
    { name: 'Felix', apiType: 'REST', docsUrl: 'https://felix.net/integrations', description: 'Vendor management & procurement API; ERP, Asite, Aconex.', supported: true },
    { name: 'Prokuria', apiType: 'REST', docsUrl: 'https://support.prokuria.com/developers/prokuria-api/', description: 'REST API; suppliers, RFPs, RFQs, eAuctions, contracts, PO.', supported: true },
    { name: 'Coupa', apiType: 'REST', docsUrl: 'https://compass.coupa.com/en-us/products/product-documentation/integration-technical-documentation/the-coupa-core-api', description: 'OAuth 2.0; POs, requisitions, invoices, suppliers, contracts, approval workflows.', supported: true },
    { name: 'WinEst / DESTINI', apiType: 'REST', docsUrl: 'https://www.beck-technology.com/destini-estimator-integrations', description: 'Construction estimating; Procore, Autodesk, Power BI integrations; cost database.', supported: true },
  ],
  field: [
    { name: 'Fieldwire', apiType: 'REST', docsUrl: 'https://developers.fieldwire.com/', description: 'Plans, tasks, forms, bulk import; US/EU base URLs.', supported: true },
    { name: 'Fulcrum', apiType: 'REST', docsUrl: 'https://docs.fulcrumapp.com/', description: 'REST & Query API; mobile forms, records, PostGIS; webhooks.', supported: true },
    { name: 'PlanGrid', apiType: 'REST', docsUrl: 'https://developer.plangrid.com/', description: 'Plans and field data; partner/customer API access.', supported: true },
    { name: 'Procore', apiType: 'REST', docsUrl: 'https://developers.procore.com/', description: 'Field tools, drawings, daily log via Procore API.', supported: true },
    { name: 'Autodesk Construction Build', apiType: 'REST', docsUrl: 'https://aps.autodesk.com/developer/overview/autodesk-construction-cloud', description: 'ACC Build; Construction Cloud APIs for field and design. Already supported.', supported: true },
    { name: 'RiskCast', apiType: 'REST', docsUrl: 'https://riskcast.happyfox.com/home/', description: 'Construction timekeeping; labor hours, quantities, crew management; Autodesk Build integration.', supported: true },
    { name: 'HCSS (HeavyJob / Stratus)', apiType: 'REST', docsUrl: 'https://developer.hcssapps.com/', description: 'HeavyJob, HeavyBid, Equipment360, Safety APIs; timecards, cost codes, equipment; OAuth 2.0 scopes.', supported: true },
  ],
  documents: [
    { name: 'SharePoint', apiType: 'REST', docsUrl: 'https://learn.microsoft.com/en-us/sharepoint/dev/apis/sharepoint-rest-graph', description: 'Microsoft Graph & SharePoint REST; sites, lists, drives, document libraries.', supported: true },
    { name: 'ShareDrive', apiType: 'REST', docsUrl: '#', description: 'Local intranet drive (network shared drive); file access and document workflows on your corporate network.', supported: true, customLink: true },
    { name: 'Google Drive (Shared Drives)', apiType: 'REST', docsUrl: 'https://developers.google.com/workspace/drive/api/guides/manage-shareddrives', description: 'Google Drive API for shared drives, files, permissions; cloud doc workflows.', supported: true },
    { name: 'Egnyte', apiType: 'REST', docsUrl: 'https://developers.egnyte.com/docs/read/Home', description: 'File System API, search, permissions, versions, events, AI summarization; construction docs.', supported: true },
    { name: 'DWG / DXF Parsing', apiType: 'REST + File', docsUrl: 'https://aps.autodesk.com/en/docs/design-automation/v3/developers_guide/overview/', description: 'Local DXF parsing (Node.js) + Autodesk Design Automation API for DWG; entity extraction, layers, title blocks.', supported: true },
  ],
  asset: [
    { name: 'Hilti ON!Track Unite', apiType: 'REST', docsUrl: 'https://unite.ontrack3.hilti.com/developer/guides', description: 'Open API; assets, workers, locations; OAuth; Fieldwire, Trackunit, Procore integrations.', supported: true },
    { name: 'Viewpoint Vista', apiType: 'REST', docsUrl: 'https://developer.trimble.com/docs/viewpoint/introduction', description: 'Equipment management module in Vista API.', supported: true },
    { name: 'Trackunit', apiType: 'REST', docsUrl: 'https://developer.trackunit.com/', description: 'Telematics and equipment data; integrates with ON!Track, Procore.', supported: true },
  ],
  safety: [
    { name: 'Blackline Safety', apiType: 'REST', docsUrl: 'https://www.blacklinesafety.com/solutions/software/api', description: 'PUSH API (alerts), CONNECT API (device data); safety monitoring.', supported: true },
    { name: 'Intelex', apiType: 'REST', docsUrl: 'https://www.intelex.com/products/applications/api', description: 'EHS & quality management system with API.', supported: true },
    { name: 'Locus Technologies', apiType: 'REST', docsUrl: 'https://www.locustec.com/api-integrations/', description: 'REST APIs for EHS data; ERP, BI, GIS, IoT.', supported: true },
    { name: 'Observia AI', apiType: 'REST', docsUrl: 'https://observia.ai/integrations/', description: 'APIs for EHS (Enablon, Cority, Intelex), Power BI, Tableau.', supported: true },
  ],
  safetyEvents: [
    { name: 'Intenseye', apiType: 'REST', docsUrl: 'https://intenseye.com/integrations/api-docs', description: 'Safety event & incident APIs; automated compliance, structured JSON.', supported: true },
    { name: 'SafetyChain', apiType: 'REST', docsUrl: 'https://developers.safetychain.com/', description: 'RDE APIs for records, BI; supplier onboarding; process APIs (forms, tasks).', supported: true },
    { name: 'SafetyCulture (iAuditor)', apiType: 'MCP + REST', docsUrl: 'https://developers.safetyculture.com/', description: 'Inspections, incidents, actions; MCP server + Developer Portal API.', supported: true },
    { name: 'Emex', apiType: 'REST', docsUrl: 'https://emex.com/api/', description: 'IoT/sensor, ICS/SCADA, environmental equipment; compliance reporting.', supported: true },
    { name: 'AIC One Global', apiType: 'REST', docsUrl: 'https://aione.global/solutions/', description: 'Incident management suite; claims, corrective actions, OSHA KPIs, real-time alerts.', supported: true },
  ],
  crm: [
    { name: 'HubSpot', apiType: 'MCP + REST + GraphQL', docsUrl: 'https://developers.hubspot.com/docs/guides/api/crm/understanding-the-crm', description: 'Native MCP server; CRM APIs, webhooks, custom objects; construction use cases.', supported: true },
    { name: 'Salesforce', apiType: 'MCP + REST', docsUrl: 'https://developer.salesforce.com/docs/apis', description: 'Agentforce MCP; Sales Cloud API; Procore/HubSpot integration.', supported: true },
    { name: 'Procore', apiType: 'REST', docsUrl: 'https://developers.procore.com/', description: 'CRM module and pipeline data via Procore API.', supported: true },
    { name: 'Follow-up CRM', apiType: 'REST', docsUrl: 'https://www.followupcrm.com/', description: 'Construction-focused CRM; pipeline, bids, contacts; API for integrations.', supported: true },
  ],
  training: [
    { name: 'Litmos', apiType: 'REST', docsUrl: 'https://www.litmos.com/learning-management-system/api', description: 'LMS REST API; courses, users, teams, learning paths, ILT sessions; certification tracking.', supported: true },
  ],
  workforce: [
    { name: 'Sage HRMS', apiType: 'REST', docsUrl: 'https://www.criterionhcm.com', description: 'Criterion HCM; payroll, HR, labor cost allocation, benefits; native Sage Intacct integration.', supported: true },
    { name: 'Nomadis', apiType: 'REST', docsUrl: 'https://nomadis.co/integrations/', description: 'Workforce billeting & housing; room assignments, rosters, travel, catering, camp management.', supported: true },
  ],
  custom: [
    { name: 'PCDB', apiType: 'Database + REST', docsUrl: '#', description: 'In-house project controls database; earned value (CPI/SPI), WBS, schedule variance, progress reporting.', supported: true, customLink: true },
    { name: 'Custom (in-house built)', apiType: 'REST / GraphQL / MCP', docsUrl: '#', description: 'Any in-house or niche system with REST, GraphQL, or MCP support can be integrated. We can connect to your internal APIs, data lakes, or custom tools.', customLink: true },
    { name: 'Niche & vertical tools', apiType: 'Varies', docsUrl: '#', description: 'Specialty estimators, takeoff tools, scheduling, BIM integrations, and regional platforms. Many expose REST or partner APIs??ist your stack for a compatibility check.', customLink: true },
  ],
};

function SoftwareCard({ item }) {
  const isMCP = item.apiType && item.apiType.includes('MCP');
  const isREST = item.apiType && (item.apiType.includes('REST') || item.apiType.includes('Interfaces') || item.apiType.includes('GraphQL'));
  const isSupported = item.supported || ALREADY_SUPPORTED_NAMES.has(item.name);
  const hasExternalLink = !item.customLink && item.docsUrl && item.docsUrl !== '#';
  const content = (
    <>
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="font-semibold text-gray-900">{item.name}</span>
        {hasExternalLink && <ExternalLink className="h-4 w-4 text-gray-400 flex-shrink-0" />}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {isSupported && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle2 className="h-3 w-3" /> Supported
          </span>
        )}
        {isREST && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
            <Code className="h-3 w-3" /> API
          </span>
        )}
        {isMCP && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
            <Cpu className="h-3 w-3" /> MCP
          </span>
        )}
      </div>
      <p className="text-sm text-gray-600">{item.description}</p>
    </>
  );
  if (hasExternalLink) {
    return (
      <a
        href={item.docsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-lg border border-gray-200 bg-white p-4 hover:border-blue-300 hover:shadow-md transition-all text-left"
      >
        {content}
      </a>
    );
  }
  return (
    <div className="block rounded-lg border border-gray-200 bg-white p-4 text-left">
      {content}
    </div>
  );
}

const SkillGrid = () => {
  const [activeCategory, setActiveCategory] = useState('overview');
  const [sidebarHidden, setSidebarHidden] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(SKILL_GRID_NAV_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (typeof data.sidebarHidden === 'boolean') setSidebarHidden(data.sidebarHidden);
    } catch (_) {}
  }, []);

  const persist = useCallback((updates) => {
    try {
      const prev = (() => {
        try {
          const raw = localStorage.getItem(SKILL_GRID_NAV_KEY);
          return raw ? JSON.parse(raw) : {};
        } catch (_) { return {}; }
      })();
      localStorage.setItem(SKILL_GRID_NAV_KEY, JSON.stringify({ ...prev, ...updates }));
    } catch (_) {}
  }, []);

  const toggleSidebar = () => {
    setSidebarHidden(prev => {
      persist({ sidebarHidden: !prev });
      return !prev;
    });
  };

  const category = CATEGORIES.find(c => c.id === activeCategory) || CATEGORIES[0];
  const showOverview = activeCategory === 'overview';

  const categoriesToShow = showOverview
    ? CATEGORIES.filter(c => c.id !== 'overview')
    : [category];
  const itemsByCategory = showOverview
    ? categoriesToShow.map(c => ({ category: c, items: SOFTWARE_BY_CATEGORY[c.id] || [] }))
    : [{ category, items: SOFTWARE_BY_CATEGORY[activeCategory] || [] }];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggleSidebar}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            title={sidebarHidden ? 'Show sidebar' : 'Hide sidebar'}
          >
            {sidebarHidden ? (
              <PanelLeftOpen className="h-5 w-5" />
            ) : (
              <PanelLeftClose className="h-5 w-5" />
            )}
          </button>
          <LayoutGrid className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Construction API & MCP Skill Grid</h1>
            <p className="text-xs text-gray-500">PM, planning, ERP, procurement, field, documents, asset, safety, events, CRM, training, workforce, custom</p>
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row min-h-0 relative">
        {sidebarHidden && (
          <button
            type="button"
            onClick={toggleSidebar}
            className="fixed left-0 top-1/2 -translate-y-1/2 z-20 mt-2 ml-0 py-3 px-1.5 rounded-r-lg bg-white border border-l-0 border-gray-200 shadow-md text-gray-500 hover:bg-gray-50 hover:text-gray-700"
            title="Show sidebar"
          >
            <PanelLeftOpen className="h-5 w-5" />
          </button>
        )}
        {!sidebarHidden && (
          <nav className="lg:w-56 flex-shrink-0 bg-white border-b lg:border-b-0 lg:border-r border-gray-200 p-3 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-y-auto">
            {CATEGORIES.map(c => {
              const Icon = c.icon;
              const isActive = activeCategory === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActiveCategory(c.id)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-left whitespace-nowrap transition-colors ${
                    isActive ? 'bg-blue-100 text-blue-800 border border-blue-200' : 'text-gray-700 hover:bg-gray-100 border border-transparent'
                  }`}
                >
                  <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
                  <span className="font-medium text-sm truncate">{c.label}</span>
                </button>
              );
            })}
          </nav>
        )}

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">{category.label}</h2>
            <p className="text-sm text-gray-500 mb-4">{category.description}</p>
            <p className="text-xs text-gray-500 mb-4">
              Software with open or public REST APIs or MCP (Model Context Protocol). Connect these for unified dashboards, AI assistants, and complex SOP/workflow automation.
            </p>
            <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2 mb-6">
              <strong>All systems supported</strong> &mdash; every listed system has a skill with API documentation ready. Includes: Procore, Autodesk Construction Cloud, Fieldwire, Vista, Fulcrum, SharePoint, ShareDrive, Follow-up CRM, ACC Build, Sage Intacct, Sage HRMS, Coupa, RiskCast, AIC One Global, Litmos, WinEst/DESTINI, Nomadis, PCDB, Egnyte, Touchplan, PlanGrid, ConstructionOnline, Oracle Primavera P6, Primavera Cloud, Bentley SYNCHRO, Microsoft Project, Oracle Aconex, QuickBooks Online, Ramp, Sage 300 CRE, Yardi Voyager, BuildingConnected, Felix, Prokuria, Google Drive, Hilti ON!Track, Trackunit, Blackline Safety, Intelex, Locus Technologies, Observia AI, Intenseye, SafetyChain, SafetyCulture, Emex, HubSpot, Salesforce, HCSS (HeavyJob/Stratus), Penta ERP, DWG/DXF Parsing. Shown with a green &quot;Supported&quot; badge below.
            </p>
            {itemsByCategory.map(({ category: cat, items }) => {
              const CatIcon = cat.icon;
              return (
              <div key={cat.id} className="mb-10">
                {showOverview && (
                  <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <CatIcon className="h-4 w-4 text-gray-600" />
                    {cat.label}
                  </h3>
                )}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((item, i) => (
                    <SoftwareCard key={`${item.name}-${i}`} item={item} />
                  ))}
                </div>
                {items.length === 0 && (
                  <p className="text-gray-500 text-sm">No software listed for this category.</p>
                )}
              </div>
            );
            })}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SkillGrid;
